// @ts-check

/**
 * @typedef {Object} IntersectionObserverOptions
 * @property {number} threshold
 * @property {Element|null} [root]
 * @property {string} [rootMargin]
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
 */

class ViewportObserver {
  /**
   * @param {Object} options
   * @param {(element: Element, extra: any) => void} [options.visibleCallback]
   * @param {(element: Element, extra: any) => void} [options.hiddenCallback]
   * @param {IntersectionObserverOptions} options.intersectionObserverOptions
   */
  constructor({
    visibleCallback,
    hiddenCallback,
    intersectionObserverOptions
  }) {
    this.visibleCallback = visibleCallback
    this.hiddenCallback = hiddenCallback
    // Bind event handlers to class instance
    this.onElementVisible = this.onElementVisible.bind(this)
    this.onIntersectionChange = this.onIntersectionChange.bind(this)
    // Initialize instance state
    /** @type {WeakMap<Element, any>} */
    this.elements = new WeakMap()
    // https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#Intersection_observer_options
    this.observer = new IntersectionObserver(
      this.onIntersectionChange,
      intersectionObserverOptions
    )
  }

  // API

  /**
   * @param {Element} element
   * @param {any} extra
   */
  observe(element, extra) {
    this.elements.set(element, extra)
    this.observer.observe(element)
  }

  /**
   * @param {Element} element
   */
  unobserve(element) {
    this.observer.unobserve(element)
    this.elements.delete(element)
  }

  // Event handlers

  /**
   * @param {Array.<IntersectionObserverEntry>} entries
   */
  onIntersectionChange(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.onElementVisible(entry.target)
      } else {
        this.onElementHidden(entry.target)
      }
    })
  }

  /**
   * @param {Element} element
   */
  onElementVisible(element) {
    if (this.visibleCallback && this.elements.has(element)) {
      const extra = this.elements.get(element)
      this.visibleCallback(element, extra)
    }
  }

  /**
   * @param {Element} element
   */
  onElementHidden(element) {
    if (this.hiddenCallback && this.elements.has(element)) {
      const extra = this.elements.get(element)
      this.hiddenCallback(element, extra)
    }
  }
}

module.exports = ViewportObserver
